import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";
import InputBox from "elements/InputBox";
import LiveWinTicker from "elements/LiveWinTicker";
import ProviderLogo from "components/ProviderLogo";
import {
  getProviderDisplayName,
  getProviderSlug,
  normalizeProviders,
} from "utils/providers";
import { resolveProviderLogo } from "utils/providerLogos";

export default function Providers(props) {
  const [searchQuery, setSearchQuery] = useState("");
  const providers = useMemo(
    () => normalizeProviders(Array.isArray(props.providers) ? props.providers : []),
    [props.providers]
  );
  const providersLoading = Boolean(props.providersLoading);

  const filteredProviders = useMemo(() => {
    const withLogo = providers.filter((provider) => resolveProviderLogo(provider));
    if (!searchQuery.trim()) return withLogo;
    const q = searchQuery.toLowerCase().trim();
    return withLogo.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.provider_slug && p.provider_slug.toLowerCase().includes(q)) ||
        (p.provider_id && String(p.provider_id).toLowerCase().includes(q))
    );
  }, [providers, searchQuery]);

  return (
    <>
      <Helmet>
        <title>{__("PC Play365 Casino | Game Providers - Pragmatic Play, NetEnt, Evolution & More")}</title>
        <meta
          name="description"
          content={__(
            "Browse all game providers at PC Play365 Casino. Discover slots and table games from Pragmatic Play, NetEnt, Evolution, Hacksaw Gaming, Microgaming and more."
          )}
        />
      </Helmet>

      <div className="container-xxl">
        <div className="providersContainer slideUpAnimation">
          <section className="providers-directory-section">
            <div className="section_headline d-flex providers-section-head">
              <div className="headline_text">
                <i className="fas fa-dice-d20" aria-hidden="true"></i>
                {__("Providers")}
              </div>

              <div className="filter-area providers-search-area">
                <div className="input_item">
                  <InputBox
                    className="form-control name_value provider-search-input"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    type="text"
                    placeholder={__("Search")}
                  />
                </div>
              </div>
            </div>

            <div className="providers-grid">
              {providersLoading && (
                <div className="providers-loading-state">{__("Loading providers...")}</div>
              )}
              {!providersLoading &&
                filteredProviders.map((provider) => {
                  const slug = getProviderSlug(provider);
                  const gameCount = provider.gameCount ?? provider.game_count ?? 0;
                  return (
                    <Link
                      key={provider.provider_id}
                      to={`/providers/${slug}`}
                      className="provider-card"
                      hreflang={Translator.getSelectedLanguage().code}
                    >
                      <div className="provider-card-inner">
                        <ProviderLogo provider={provider} />
                        <div className="provider-info">
                          <span className="provider-name">{getProviderDisplayName(provider)}</span>
                          {gameCount > 0 && (
                            <span className="provider-game-count">
                              {gameCount} {__("games")}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>

            {!providersLoading && filteredProviders.length === 0 && (
              <div className="providers-empty-state">
                {searchQuery
                  ? __("No providers match your search. Try a different term.")
                  : __("No providers available.")}
              </div>
            )}
          </section>

          <LiveWinTicker {...props} />
          <br />
        </div>
      </div>
    </>
  );
}
